import { useInfiniteQuery } from "@tanstack/react-query";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useState, useCallback, useMemo } from "react";

// Define types for the API response
interface MetaData {
  current_page: number;
  last_page: number;
}

interface APIResponse<T> {
  data: T[];
  meta: MetaData;
}

interface useInfiniteScrollRepresentativeFetcher<T> {
  queryKey: string; // Added queryKey to the interface
  fetchData: (params: any) => Promise<{ data: APIResponse<T> }>;
  initialParams: any;
}

function useInfiniteScrollRepresentativeFetcher<T>({
  queryKey,
  fetchData,
  initialParams,
}: useInfiniteScrollRepresentativeFetcher<T>) {
  const [params, setParams] = useState(initialParams);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [queryKey, params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchData({
        ...params,
        page: pageParam,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array of items
  const items = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);

  // Update params and reset query
  const updateParams = useCallback(
    (newParams: Partial<typeof initialParams>) => {
      setParams((prev: any) => ({
        ...prev,
        ...newParams,
        page: 1,
      }));
    },
    []
  );

  const loading = isLoading || isFetchingNextPage;

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: loading,
  });

  return {
    items,
    sentryRef,
    loading,
    updateParams,
    refetch,
  };
}

export default useInfiniteScrollRepresentativeFetcher;
